// Her COURSE: modules she owns, seeded once from the curriculum she locked in
// My Offer.
//
// THE CURRICULUM STAYS IN MY OFFER. It is what she markets from — My Positioning,
// the website and social copy all read it — so it is never moved here and never
// written back. This pulls a copy, the way the Offer Ladder pulls her Signature
// Offer, and from that moment the modules are hers to rewrite.
//
// A MODULE IS NOT A WEEK. `week_number` is a nullable leftover on the table (see
// supabase/0007); ordering is `sort_order`, because a module is sequenced but not
// dated — when she runs it is decided wherever the course is hosted.
//
// WHY THIS NEEDS A SEEDED FLAG AND WEBSITE PAGES DON'T
// `website_pages` is unique on (clerk_user_id, slug), so a second browser's
// insert fails instead of duplicating. Modules have no natural key — two browsers
// open at once would each mint fresh ids and she'd get her whole curriculum
// twice. So the flag is the guard, exactly as it is for offers, and it lives in
// `settings.course_seeded` rather than localStorage so it holds across her
// devices.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { reorderWrites } from "./reorder";
import { createRecordStore } from "./recordStore";
import { hashSource } from "./sourceHash";
import { curriculumModules, curriculumText, hasLockedCurriculum, seededBlockOrder } from "./curriculumParse";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
// `sortOrder`, `prework`, `blockOrder`, `source*` are added by supabase/0007.
// `courseId` by supabase/0015 — which course this module belongs to. Nullable
// in Postgres; 0015's backfill filled it for every module that existed.
export const COURSE_MODULE_FIELDS = [
  "title", "sortOrder", "status", "blockOrder", "courseId",
  "outcome", "teachingOutline", "discussionQuestions", "homework", "prework",
  "archetypeNote", "weekNumber",
  "source", "sourceText", "sourceHash",
];

export const COURSE_SOURCE = "accelerator";

/* A curriculum entry and a module she already has are THE SAME ONE when they
   came from the same week of her curriculum. `weekNumber` is kept on every
   seeded module purely as provenance — this is what it's for.

   Falls back to the title, trimmed and case-folded, because a module seeded
   before `week_number` was recorded (or one she made herself by hand with the
   curriculum's name) is still that module as far as she's concerned, and
   pulling it again would give her two rows with the same name. */
export function sameCurriculumModule(entry, module) {
  const week = entry?.sourceWeek;
  if (week != null && module?.weekNumber != null) return Number(module.weekNumber) === Number(week);
  const a = String(entry?.title || "").trim().toLowerCase();
  const b = String(module?.title || "").trim().toLowerCase();
  return !!a && a === b;
}

/** The curriculum entries that have no module yet, in her curriculum's order. */
export function missingFromCourse(entries = [], modules = []) {
  return entries.filter((e) => !modules.some((m) => sameCurriculumModule(e, m)));
}

/**
 * The modules of one course.
 *
 * The mirror of `pagesOfSite`, and pulled out for the same reason: a module
 * that belongs to no list is in Supabase, saved, and on no screen — which is
 * not something looking at the app would ever show you.
 *
 * `courseId` null means the courses have not loaded yet, so the answer is
 * empty and the panel says it is loading. `adoptUncoursed` is true for her
 * first course only, which is where a module written with no course shows up
 * rather than vanishing.
 */
export function modulesOfCourse(modules = [], courseId = null, adoptUncoursed = false) {
  if (!courseId) return [];
  return (modules || []).filter((m) => m?.courseId === courseId || (adoptUncoursed && !m?.courseId));
}

/** Modules in her order. Sorted here as well as in the query: `recordStore.save()`
 *  replaces a row in place, so reordering wrote to Supabase and left the array
 *  alone — the bug that made the website's arrows look dead. */
export function byModuleOrder(modules = []) {
  return [...(modules || [])].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
}

/** The next free slot at the bottom. */
export function nextModuleOrder(modules = []) {
  return (modules || []).reduce((max, m) => Math.max(max, (m?.sortOrder ?? 0) + 1), 0);
}

/** Has her curriculum moved since these modules were pulled from it? Answered
 *  from the stored hash rather than a diff, so an edit SHE made to a module can
 *  never read as the source having changed. */
export function curriculumChanged(modules = [], offerOutputs = {}) {
  const current = curriculumText(offerOutputs);
  if (!current) return false;
  const hash = hashSource(current);
  return (modules || []).some((m) => m?.source === COURSE_SOURCE && m.sourceHash && m.sourceHash !== hash);
}

const useModuleRecords = createRecordStore({
  table: "lesson_plans",
  fields: COURSE_MODULE_FIELDS,
  idPrefix: "lsn",
  orderBy: "sort_order",
  ascending: true,
});

/**
 * @param offerOutputs  her locked My Offer sections, from App's mergedOutputs
 * @param courseSeeded  settings.course_seeded — has the once-ever pull happened
 * @param markSeeded    persists that flag
 * @param enabled       gate for the automatic pull. Callers that don't yet know
 *                      the flag must leave this false, or the pull runs against
 *                      an unknown state and can duplicate her whole curriculum.
 */
export function useCourseModules({
  offerOutputs = {}, courseId = null, adoptUncoursed = false,
  courseSeeded = false, markSeeded, enabled = true,
} = {}) {
  const { rows, loading, error, save, remove, reload, clearError, newId } = useModuleRecords();

  const ordered = useMemo(() => byModuleOrder(rows), [rows]);

  /* Narrowed to the course she is standing in. Every row loads in one query —
     the table shows a word count and a block state per module, so a query per
     course would be a request inside a render loop.

     Null while the courses are still loading, and then the list is empty on
     purpose: the panel says "Loading your modules…" rather than flashing "no
     modules", which reads as loss.

     A module with NO course shows up under her FIRST course, for the same
     reason an unsited page does: `course_id` is nullable, and a module written
     by a browser still running the code from before the switcher would
     otherwise belong to no list and appear on no screen, silently. */
  const modules = useMemo(
    () => modulesOfCourse(ordered, courseId, adoptUncoursed),
    [ordered, courseId, adoptUncoursed],
  );

  /* Parsed ONCE per change of her locked curriculum, not per render — this is a
     regex pass over her prose, and the panel reads `missingCount` on every draw.
     Split in two so a module saving (which makes a new `modules` array) re-runs
     only the cheap filter, never the parse. */
  const curriculumEntries = useMemo(() => curriculumModules(offerOutputs), [offerOutputs]);
  const missingEntries = useMemo(() => missingFromCourse(curriculumEntries, modules), [curriculumEntries, modules]);

  /** One module from a parsed curriculum week. Seeded modules arrive with only
   *  the blocks the curriculum actually gave them — an outcome and a homework.
   *  The outline, questions and pre-work are what Brill drafts next; listing them
   *  empty would make every module read as half-broken rather than started. */
  const moduleFromCurriculum = useCallback((m, from) => ({
    id: newId(),
    title: m.title || "Untitled module",
    sortOrder: m.order,
    status: "draft",
    blockOrder: seededBlockOrder(m),
    outcome: m.outcome || "",
    homework: m.homework || "",
    teachingOutline: [],
    discussionQuestions: [],
    prework: "",
    /* Kept for provenance only. Course has no weeks; this records which week of
       her curriculum the module came from, which is the honest answer to "where
       did this come from" without putting a calendar back on screen. */
    weekNumber: m.sourceWeek ?? null,
    source: COURSE_SOURCE,
    sourceText: from,
    sourceHash: from ? hashSource(from) : null,
  }), [newId]);

  /**
   * The pull. Returns the modules created — [] when there was nothing to pull
   * AND when everything was already here, which the caller tells apart by asking
   * `missingFromCourse` first.
   *
   * IT ONLY ADDS WHAT'S MISSING. It used to add the whole curriculum every time,
   * so pressing "Pull from My Offer" after the automatic pull gave her the same
   * twelve modules twice (reported 2026-08-18). A curriculum entry she already
   * has is skipped, which makes the button idempotent: it is "bring across
   * anything that isn't here yet", and pressing it twice does nothing the second
   * time.
   *
   * Sequential rather than parallel: a failure halfway leaves her with the
   * modules that landed rather than an unknowable mix.
   */
  const pulling = useRef(false);
  const pullCurriculum = useCallback(async () => {
    if (pulling.current) return [];
    const missing = missingEntries;
    if (!missing.length) return [];
    pulling.current = true;
    const from = curriculumText(offerOutputs);
    const base = nextModuleOrder(modules);
    const made = [];
    try {
      for (const m of missing) {
        /* Appended in her curriculum's own order, at the end of what she has.
           A module she deleted and pulled back comes in at the bottom rather
           than shouldering into its old position — the arrows are there, and
           quietly renumbering the modules around it would be worse. */
        const saved = await save({ ...moduleFromCurriculum(m, from), courseId, sortOrder: base + m.order });
        if (saved) made.push(saved);
      }
    } finally {
      pulling.current = false;
    }
    return made;
  }, [offerOutputs, modules, missingEntries, save, moduleFromCurriculum, courseId]);

  /* The automatic pull, once ever. Guards, each load-bearing:
       - `enabled` / `loading` — pulling against a list that hasn't arrived, or a
         flag we haven't read, duplicates her whole curriculum.
       - `error` — a failed load leaves `rows` empty, which is NOT an empty table.
       - `courseSeeded` + `attempted` — the flag is the real guard across devices;
         the ref stops a second run inside this session before the flag lands.
       - a locked curriculum to pull — empty modules appearing on their own are
         clutter, not a head start. */
  const attempted = useRef(false);
  useEffect(() => {
    if (!enabled || loading || error || courseSeeded || attempted.current) return;
    /* Nowhere to put them yet. Pulling before the courses have loaded would
       write a whole curriculum with no `course_id`, which is the one state this
       tool can no longer show without the adoption rule above. */
    if (!courseId) return;
    if (!hasLockedCurriculum(offerOutputs)) return;
    attempted.current = true;
    pullCurriculum()
      .then((made) => { if (made.length) markSeeded?.(); })
      .catch(() => { attempted.current = false; });
  }, [enabled, loading, error, courseSeeded, courseId, offerOutputs, pullCurriculum, markSeeded]);

  /** A blank module at the bottom. Hers from the start — no source, so a refresh
   *  from the curriculum leaves it alone. */
  const addModule = useCallback(async () => save({
    id: newId(),
    courseId,
    title: "",
    sortOrder: nextModuleOrder(modules),
    status: "draft",
    blockOrder: [],
    outcome: "", homework: "", prework: "",
    teachingOutline: [], discussionQuestions: [],
    source: null,
  }), [modules, save, newId]);

  /** Move a module to an arbitrary position, which is what a drag produces.
   *
   *  Renumbers the course 0..n-1 in the new order rather than swapping
   *  neighbours — see utils/reorder.js for why, including what it repairs on
   *  rows written before 0007. Only the modules that actually moved are
   *  written.
   *
   *  Sequential rather than parallel: `save` replaces rows in local state one at
   *  a time, and racing them can interleave writes onto a stale copy. */
  const reorderModules = useCallback(async (from, to) => {
    let ok = true;
    for (const m of reorderWrites(modules, from, to)) if (!(await save(m))) ok = false;
    return ok;
  }, [modules, save]);

  /** Add a block to a module, in the order she added it. */
  const addBlock = useCallback(async (module, kindId) => {
    const order = Array.isArray(module?.blockOrder) ? module.blockOrder : [];
    if (!kindId || order.includes(kindId)) return module;
    return save({ ...module, blockOrder: [...order, kindId] });
  }, [save]);

  /** Remove a block. Its column is cleared in the same write — a block she
   *  removed must not come back with its old words if she adds it again. */
  const removeBlock = useCallback(async (module, kindId, cleared) => {
    const order = (Array.isArray(module?.blockOrder) ? module.blockOrder : []).filter((k) => k !== kindId);
    return save({ ...module, ...cleared, blockOrder: order });
  }, [save]);

  return {
    modules, loading, error, clearError, reload,
    /* EVERY row, not just this course's. The landing table counts the
       children of each course, so it needs the whole list — and it costs
       nothing, because the store has always loaded every row and
       filtered in memory. Exposing it beats a second query per row
       inside a render loop, which is the shape check 4 exists to
       catch. */
    allModules: ordered,
    saveModule: save,
    deleteModule: remove,
    newModuleId: newId,
    addModule,
    reorderModules,
    addBlock,
    removeBlock,
    pullCurriculum,
    canPull: hasLockedCurriculum(offerOutputs),
    /* How many modules the button would actually bring across. The panel needs
       this to say what happened — a pull that adds nothing has to read as
       "everything's already here" rather than as a button that did nothing. */
    missingCount: missingEntries.length,
    changed: curriculumChanged(modules, offerOutputs),
  };
}
